import React, { useEffect, useState, useMemo, useContext } from 'react';

import { alertError } from '@src/lib/alert';
import SentryUtils from '@src/lib/sentry';
import { Maintenance } from '@src/components/Maintenance';
import Tabs from '@modules/common/components/Tabs';
import { fetchWithHandling } from '@src/utils/fetching';

import ClientResource from '../../../components/services/ClientResource';
import { OAuthClientDescriptor, mapClients } from '../../../components/types/oauth-client';
import ClientsList from './ClientsList';
import NewClient from '../NewClient';
import Header from './Header';
import { AuthenticationContext } from '../../../components/context';

type Props = {
  ownerId: string;
  limit: number | undefined;
};

function ExistingClients({ ownerId, limit }: Props) {
  const [fetchingClients, setFetchingClients] = useState<boolean>(true);
  const [clients, setClients] = useState<OAuthClientDescriptor[]>([]);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setFetchingClients(true);
    const MAX_PAGES = 100;
    const clientsList: OAuthClientDescriptor[] = [];

    try {
      for (let i = 0; i < MAX_PAGES; i += 1) {
        // eslint-disable-next-line
        const resp = await fetchWithHandling(() =>
          ClientResource.search({
            accountId: ownerId,
            viewtoken: i * 100,
            count: 100,
          }),
        );
        if (!resp) {
          alertError('Error fetching OAuth Clients');
          break;
        }
        if (resp.member.length === 0) {
          break;
        }
        clientsList.push(...mapClients(resp.member));
      }
      setClients(clientsList);
    } catch (error) {
      const errorMessage = 'Something went wrong while fetching OAuth Clients';
      alertError(errorMessage);
      SentryUtils.captureAPIError(error, errorMessage);
    }
    setFetchingClients(false);
  };

  const { activeClients, inactiveClients } = useMemo(() => {
    const activeClients = clients.filter(client => !client.isDeleted);
    activeClients.sort((a, b) => (a.name > b.name ? 1 : -1));
    return {
      activeClients,
      inactiveClients: clients.filter(client => client.isDeleted),
    };
  }, [clients]);

  const tabs = useMemo(
    () => [
      {
        title: 'Active',
        content: <ClientsList editable clients={activeClients} onClientUpdated={fetchClients} />,
      },
      {
        title: 'Inactive',
        content: <ClientsList clients={inactiveClients} />,
      },
    ],
    [activeClients, inactiveClients, fetchClients],
  );

  const { readonly, maintenanceInProgress } = useContext(AuthenticationContext);
  return (
    <>
      <Header />
      {maintenanceInProgress && <Maintenance service="managing OAuth2 clients" />}
      {!readonly && (
        <NewClient
          onClientCreated={fetchClients}
          activeClientsCount={activeClients.length}
          ownerId={ownerId}
          limit={limit}
        />
      )}
      <Tabs loading={fetchingClients} tabs={tabs} />
    </>
  );
}

export default ExistingClients;
